import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  TouchableOpacity,
  Alert,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { format, parseISO } from 'date-fns';
import Icon from 'react-native-vector-icons/Ionicons';

import { RootState, AppDispatch } from '@/store';
import {
  getTimeOffDetail,
  acceptSub,
  declineSub,
  clearDetail,
  ActionResponse,
} from '@/store/slices/timeOffSlice';
import { ScheduleStackParamList } from '@/types/navigation';
import { colors, font, space, radius, shadow } from '@/theme/chalkCourt';

type RespondRoute = RouteProp<ScheduleStackParamList, 'TimeOffRespond'>;

type Outcome = 'idle' | 'accepted' | 'declined' | 'alreadyResponded' | 'actionError';

const fmtEventDate = (date: string): string => {
  try {
    return format(parseISO(date), 'EEE, MMM d');
  } catch {
    return date;
  }
};

/**
 * The assigned substitute's Accept / Decline screen for a cover request,
 * reached from a `TIMEOFF_SUB` push tap. Identity is server-resolved from the
 * JWT — this screen asserts nothing about who the user is. A servlet 409
 * (already responded) resolves as a terminal state, distinct from a retryable
 * error.
 */
const RespondToSubScreen: React.FC = () => {
  const route = useRoute<RespondRoute>();
  const navigation = useNavigation<any>();
  const dispatch = useDispatch<AppDispatch>();
  const { requestId, eventId } = route.params;

  const reduxDetail = useSelector((state: RootState) => state.timeOff.detail);
  const isResponding = useSelector((state: RootState) => state.timeOff.isResponding);
  const loadError = useSelector((state: RootState) => state.timeOff.error);
  const loadErrorStatus = useSelector((state: RootState) => state.timeOff.detailErrorStatus);

  // Guard against a stale detail from a previously-viewed request.
  const detail = reduxDetail && reduxDetail.id === requestId ? reduxDetail : null;
  const event = detail?.events.find((e) => e.id === eventId) ?? null;
  const requester = detail?.employee;
  const requesterName = requester ? `${requester.firstName} ${requester.lastName}` : 'the requester';

  const [outcome, setOutcome] = useState<Outcome>('idle');
  const [actionError, setActionError] = useState<string>('');

  const load = useCallback(() => {
    dispatch(getTimeOffDetail(requestId));
  }, [dispatch, requestId]);

  useEffect(() => {
    dispatch(clearDetail());
    load();
    return () => {
      dispatch(clearDetail());
    };
  }, [dispatch, load]);

  const resolveFailure = (message: string) => {
    if (message && /already/i.test(message)) {
      setActionError(message);
      setOutcome('alreadyResponded');
    } else {
      setActionError(message || 'Could not complete your response.');
      setOutcome('actionError');
    }
  };

  // Interpret a thunk result: the forward path can RESOLVE with success:false
  // (servlet 409 already-responded / 403 not-the-sub), so we inspect the
  // payload — not just fulfilled-vs-rejected.
  const finish = (
    isFulfilled: boolean,
    payload: unknown,
    success: 'accepted' | 'declined'
  ) => {
    if (isFulfilled) {
      const p = payload as ActionResponse | undefined;
      if (p && p.success === false) {
        resolveFailure(p.message);
      } else {
        setOutcome(success);
      }
    } else {
      resolveFailure((payload as string) || 'Something went wrong');
    }
  };

  const onAccept = async () => {
    const result = await dispatch(acceptSub({ id: requestId, eventId }));
    finish(acceptSub.fulfilled.match(result), result.payload, 'accepted');
  };

  const doDecline = async () => {
    const result = await dispatch(declineSub({ id: requestId, eventId }));
    finish(declineSub.fulfilled.match(result), result.payload, 'declined');
  };

  const onDecline = () => {
    Alert.alert(
      'Decline this cover?',
      `This shift goes back to ${requesterName} to reassign.`,
      [
        { text: 'Keep it', style: 'cancel' },
        { text: 'Decline', style: 'destructive', onPress: doDecline },
      ]
    );
  };

  const onRetryAction = () => {
    setActionError('');
    setOutcome('idle');
  };

  const goBack = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate('ScheduleHome');
    }
  };

  // --- Terminal outcomes -------------------------------------------------
  if (outcome === 'accepted') {
    return (
      <ResultCard
        icon="checkmark-circle"
        tint={colors.court}
        title="You're covering it"
        body="Thanks — the shift is confirmed to you. It now shows as accepted on the schedule."
        actionLabel="DONE"
        onAction={goBack}
      />
    );
  }
  if (outcome === 'declined') {
    return (
      <ResultCard
        icon="arrow-undo-circle"
        tint={colors.accentDark}
        title="Sent back to reassign"
        body={`This shift goes back to ${requesterName} to find another sub.`}
        actionLabel="DONE"
        onAction={goBack}
      />
    );
  }
  if (outcome === 'alreadyResponded') {
    return (
      <ResultCard
        icon="information-circle"
        tint={colors.inkSoft}
        title="Already responded"
        body={actionError || 'This cover request has already been responded to.'}
        actionLabel="DONE"
        onAction={goBack}
      />
    );
  }

  // --- 403: no longer the sub on this request (cleared, reassigned, cancelled) ---
  if (!detail && loadErrorStatus === 403) {
    return (
      <View style={styles.centered}>
        <View style={styles.card}>
          <Icon name="calendar-outline" size={30} color={colors.inkFaint} />
          <Text style={styles.title}>This request is no longer active</Text>
          <Text style={styles.body}>
            You're no longer the sub for this shift — it may have been reassigned or the time off
            was cancelled.
          </Text>
          <TouchableOpacity style={styles.primaryBtn} activeOpacity={0.85} onPress={goBack}>
            <Text style={styles.primaryText}>DONE</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // --- Load error (detail never arrived) ---------------------------------
  if (!detail && loadError) {
    return (
      <View style={styles.centered}>
        <View style={styles.card}>
          <Icon name="cloud-offline-outline" size={30} color={colors.accentDark} />
          <Text style={styles.title}>Could not load this request</Text>
          <Text style={styles.body}>{loadError}</Text>
          <TouchableOpacity style={styles.primaryBtn} activeOpacity={0.85} onPress={load}>
            <Text style={styles.primaryText}>RETRY</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // --- Loading -----------------------------------------------------------
  if (!detail) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator color={colors.accent} />
        <Text style={styles.loadingText}>Loading the shift…</Text>
      </View>
    );
  }

  // --- Stale push: the event is no longer part of this request -----------
  if (!event) {
    return (
      <View style={styles.centered}>
        <View style={styles.card}>
          <Icon name="calendar-outline" size={30} color={colors.inkFaint} />
          <Text style={styles.title}>This request is no longer active</Text>
          <Text style={styles.body}>
            The shift you were asked to cover has changed or been reassigned.
          </Text>
          <TouchableOpacity style={styles.primaryBtn} activeOpacity={0.85} onPress={goBack}>
            <Text style={styles.primaryText}>DONE</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // --- Action error (retryable) ------------------------------------------
  if (outcome === 'actionError') {
    return (
      <View style={styles.centered}>
        <View style={styles.card}>
          <Icon name="alert-circle-outline" size={30} color={colors.accentDark} />
          <Text style={styles.title}>Something went wrong</Text>
          <Text style={styles.body}>{actionError}</Text>
          <TouchableOpacity style={styles.primaryBtn} activeOpacity={0.85} onPress={onRetryAction}>
            <Text style={styles.primaryText}>TRY AGAIN</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // --- Ready: Accept / Decline -------------------------------------------
  return (
    <View style={styles.screen}>
      <View style={styles.content}>
        <Text style={styles.kicker}>COVER REQUEST</Text>
        <Text style={styles.subbingFor}>Subbing for {requesterName}</Text>
        <View style={styles.rule} />

        <View style={styles.shiftCard}>
          <Text style={styles.eventName} numberOfLines={2}>
            {event.name}
          </Text>
          <Text style={styles.eventMeta}>
            {fmtEventDate(event.eventDate)} · {event.start}–{event.end}
            {event.dept ? ` · ${event.dept}` : ''}
          </Text>
        </View>
      </View>

      <View style={styles.footer}>
        <TouchableOpacity
          style={[styles.accept, isResponding && styles.busy]}
          activeOpacity={0.85}
          disabled={isResponding}
          onPress={onAccept}>
          <Text style={styles.acceptText}>{isResponding ? 'WORKING…' : 'ACCEPT'}</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.decline, isResponding && styles.busy]}
          activeOpacity={0.85}
          disabled={isResponding}
          onPress={onDecline}>
          <Text style={styles.declineText}>DECLINE</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

interface ResultCardProps {
  icon: string;
  tint: string;
  title: string;
  body: string;
  actionLabel: string;
  onAction: () => void;
}

const ResultCard: React.FC<ResultCardProps> = ({ icon, tint, title, body, actionLabel, onAction }) => (
  <View style={styles.centered}>
    <View style={styles.card}>
      <Icon name={icon} size={36} color={tint} />
      <Text style={styles.title}>{title}</Text>
      <Text style={styles.body}>{body}</Text>
      <TouchableOpacity style={styles.primaryBtn} activeOpacity={0.85} onPress={onAction}>
        <Text style={styles.primaryText}>{actionLabel}</Text>
      </TouchableOpacity>
    </View>
  </View>
);

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.bg, justifyContent: 'space-between' },
  content: { padding: space.lg },

  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.bg,
    padding: space.lg,
  },
  loadingText: {
    fontFamily: font.bodyMed,
    fontSize: 14,
    color: colors.inkSoft,
    marginTop: space.md,
  },

  kicker: {
    fontFamily: font.bodyBold,
    fontSize: 13,
    letterSpacing: 2.5,
    color: colors.accent,
    marginBottom: 2,
  },
  subbingFor: {
    fontFamily: font.display,
    fontSize: 34,
    lineHeight: 38,
    color: colors.ink,
    letterSpacing: -0.5,
  },
  rule: {
    height: 3,
    width: 48,
    backgroundColor: colors.ink,
    borderRadius: 2,
    marginTop: space.sm,
    marginBottom: space.lg,
  },

  shiftCard: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    padding: space.lg,
    borderWidth: 1,
    borderColor: colors.line,
    ...shadow.card,
  },
  eventName: {
    fontFamily: font.displayBold,
    fontSize: 26,
    color: colors.ink,
  },
  eventMeta: {
    fontFamily: font.bodyMed,
    fontSize: 14,
    color: colors.inkSoft,
    marginTop: space.xs,
  },

  card: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    padding: space.xl,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.line,
    alignSelf: 'stretch',
  },
  title: {
    fontFamily: font.displayBold,
    fontSize: 24,
    color: colors.ink,
    marginTop: space.sm,
    textAlign: 'center',
  },
  body: {
    fontFamily: font.body,
    fontSize: 14,
    lineHeight: 20,
    color: colors.inkSoft,
    textAlign: 'center',
    marginTop: space.xs,
    marginBottom: space.lg,
  },

  footer: {
    padding: space.lg,
    borderTopWidth: 1,
    borderTopColor: colors.line,
    backgroundColor: colors.bg,
  },
  accept: {
    height: 56,
    borderRadius: radius.md,
    backgroundColor: colors.accent,
    alignItems: 'center',
    justifyContent: 'center',
  },
  acceptText: {
    fontFamily: font.display,
    fontSize: 22,
    letterSpacing: 1,
    color: colors.onAccent,
  },
  decline: {
    height: 52,
    borderRadius: radius.md,
    backgroundColor: 'transparent',
    borderWidth: 1.5,
    borderColor: colors.lineStrong,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: space.md,
  },
  declineText: {
    fontFamily: font.display,
    fontSize: 20,
    letterSpacing: 1,
    color: colors.inkSoft,
  },
  busy: { opacity: 0.5 },

  primaryBtn: {
    height: 48,
    paddingHorizontal: space.xl,
    borderRadius: radius.md,
    backgroundColor: colors.accent,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryText: {
    fontFamily: font.display,
    fontSize: 20,
    letterSpacing: 1,
    color: colors.onAccent,
  },
});

export default RespondToSubScreen;
